import React from "react";
import { hydrateRoot, createRoot } from "react-dom/client";
import "@/index.css";
import App from "@/App";

// react-snap hydration handoff:
//   • On the SEO-prerendered HTML (build/<route>/index.html) the #root
//     element ships pre-populated with the static markup react-snap
//     emitted at build time. We must hydrate that markup so React can
//     attach event listeners without re-rendering and causing a flash.
//   • During `yarn start` (dev) and on any route react-snap didn't cover,
//     #root is empty and we fall back to createRoot().render().
const rootElement = document.getElementById("root");
const tree = (
    <React.StrictMode>
        <App />
    </React.StrictMode>
);

// Phase 2 root-cause + defense-in-depth for React #418 hydration warnings
// on public prerendered routes.
//
// We ship TWO layers:
//
//  1) STRUCTURAL FIXES (source of truth — cannot be added at runtime):
//     - Every framer-motion `<motion.*>` element with `initial={{...}}` on
//       a public prerendered surface carries `suppressHydrationWarning`
//       (React's official escape hatch for known-safe attribute drift).
//       Framer-motion writes inline `style="opacity:0;transform:translateY(Npx)"`
//       at prerender time; different Chromium versions normalize the style
//       attribute differently on read (compact vs whitespaced), which React
//       flags as a text-content mismatch. `suppressHydrationWarning` tells
//       React to ignore that attribute drift on the annotated element only.
//     - JSX text patterns like `text{" "}<span>rest</span>` that produced
//       an extra whitespace text node vs the HTML serializer's combined
//       text node were normalized to `{"text "}<span>rest</span>` — one
//       JSX child instead of two, so hydration matches.
//     - `&ldquo;{value}&rdquo;` entity-splitting patterns were folded into
//       single template literals `{`\u201C${value}\u201D`}` — one text
//       child, no VDOM/DOM child-count mismatch.
//
//  2) SAFETY-NET SWALLOW (this handler):
//     Silences any remaining #418 that comes from the same class of
//     framer-motion + react-snap browser-serializer differences. These are
//     RECOVERABLE errors — React auto-heals the subtree by re-rendering on
//     the client without any user-visible defect. We only swallow the four
//     known-safe args signatures:
//       - `args[]=text`     — attribute/text string drift
//       - `args[]=HTML`     — child-list re-order from Helmet/third-party
//                             `<script>` injections during prerender
//       - `args[]=comment`  — React Fragment boundary comment markers
//       - `args[]=treewrap` — child ordering under framer-motion wrappers
//     All OTHER hydration errors (args=`node`, args=`server`, args=`client`)
//     and all non-#418 errors still propagate to `console.error` so real
//     bugs remain visible.
function onRecoverableError(err) {
    const msg = String((err && err.message) || err);
    if (msg.includes("#418")) {
        const known =
            msg.includes("args[]=text") ||
            msg.includes("args[]=HTML") ||
            msg.includes("args[]=comment") ||
            msg.includes("args[]=treewrap");
        if (known) return;
    }
    // eslint-disable-next-line no-console
    console.error(err);
}

if (rootElement.hasChildNodes()) {
    hydrateRoot(rootElement, tree, { onRecoverableError });
} else {
    createRoot(rootElement).render(tree);
}
